import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import { MAAC_URL, SERVER_REGION } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useNavigate } from "@tanstack/react-router";
import type { FC } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { useEnableCustomFieldCard } from "@/features/customField/useEnableCustomFieldCard";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { useEnableGoogleAdsIntegrationMilestone1 } from "@/features/google-ads/useEnableGoogleAdsIntegration";
import { useEnableLineAdsIntegration } from "@/features/line-ads/useEnableLineAdsIntegration";
import { useEnableMetaAdsIntegration } from "@/features/meta-ads/useEnableMetaAdsIntegration";
import { useEnableProductFeedCard } from "@/features/productFeed/useEnableProductFeedCard";
import { interlude } from "@/interlude";
import {
  shouldShowGoogleAdsContactUs,
  shouldShowLineAdsContactUs,
  shouldShowMetaAdsContactUs,
} from "@/pages/applications/adContactUs";
import { ApplicationCard } from "@/pages/applications/components/ApplicationCard";
import { ECOMMERCE_PLATFORMS } from "@/pages/applications/ecommercePlatforms";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import GoogleAdsImage from "@/statics/googleAds.png";
import LineAdsBannerImage from "@/statics/lineAdsBanner.svg";
import MetaAdsImage from "@/statics/metaAds.svg";

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionDescription: {
    color: theme.colors.staticFgNote,
  },
  cardContainer: {
    display: "grid",
    gap: "16px",
    gridAutoRows: "auto",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ApplicationSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const ApplicationSection: React.FC<ApplicationSectionProps> =
  function ApplicationSection({ title, description, children }) {
    return (
      <Box component="section" sx={styles.section}>
        <Box>
          <Typography variant="h2" fontWeight={500}>
            {title}
          </Typography>
          <Box sx={styles.sectionDescription}>
            <Typography variant="body">{description}</Typography>
          </Box>
        </Box>
        <Box sx={styles.cardContainer}>{children}</Box>
      </Box>
    );
  };

/**
 * Profile unification has its own page; this card shows whether it is
 * connected and opens that page, where connecting and merge rules live.
 */
const ProfileUnificationCard: React.FC = function ProfileUnificationCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const openDataHub = useHandler(() => navigate({ to: "/data-hub" }));
  return (
    <ApplicationCard
      title={t("applications.cdh.title")}
      description={t(
        "dataHub.description",
        "One profile per customer across MAAC, CAAC and your connected channels.",
      )}
      learnMoreLink={t("applications.cdh.learnMore.link")}
      learnMoreText={t("common.learnMore")}
      icon={<PeopleAltOutlined />}
      buttonText={t("overview.dataHub.connect", "Set up")}
      onButtonClick={openDataHub}
      isConnected={unifyScopeSettingQuery.data?.isCdhConnected ?? false}
      isLoading={unifyScopeSettingQuery.isPending}
      connectedLabel={t("common.connected")}
      connectedButtonText={t("common.manage", "Manage")}
    />
  );
};

interface CustomerDataSectionProps {
  customFieldButtonText: string;
  customFieldCount: number | undefined;
  customFieldLimit: number;
  enableCustomFieldCard: boolean;
  enableProductFeedCard: boolean;
  hasCustomFields: boolean;
  isCustomFieldLoading: boolean;
  isProductFeedConnected: boolean;
  isProductFeedLoading: boolean;
  onCustomFieldClick: () => void;
  onProductFeedClick: () => void;
  productFeedCount: number | undefined;
  productFeedModuleEnabled: boolean;
}

const CustomerDataSection: React.FC<CustomerDataSectionProps> =
  function CustomerDataSection({
    customFieldButtonText,
    customFieldCount,
    customFieldLimit,
    enableCustomFieldCard,
    enableProductFeedCard,
    hasCustomFields,
    isCustomFieldLoading,
    isProductFeedConnected,
    isProductFeedLoading,
    onCustomFieldClick,
    onProductFeedClick,
    productFeedCount,
    productFeedModuleEnabled,
  }) {
    const { t } = useTranslation();

    return (
      <ApplicationSection
        title={t("applications.customerData.title", "Customer data")}
        description={t(
          "applications.customerData.description",
          "Manage how customer data is unified, structured, and imported.",
        )}
      >
        <ProfileUnificationCard />
        {enableProductFeedCard ? (
          <ApplicationCard
            title={t("applications.productFeed.title")}
            description={t("applications.productFeed.description")}
            detailText={
              productFeedCount === undefined
                ? undefined
                : t("applications.productFeed.configuredCount", {
                    count: productFeedCount,
                    defaultValue: "Configured feeds: {{count}}",
                  })
            }
            learnMoreLink={t("applications.productFeed.learnMore.link")}
            learnMoreText={t("common.learnMore")}
            buttonText={
              isProductFeedConnected
                ? t("common.manage", "Manage")
                : productFeedModuleEnabled
                  ? t("common.connect")
                  : t("common.contactUs")
            }
            onButtonClick={onProductFeedClick}
            icon={<Inventory2Outlined />}
            isConnected={isProductFeedConnected}
            isLoading={isProductFeedLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableCustomFieldCard ? (
          <ApplicationCard
            title={t("applications.customField.title")}
            description={t("applications.customField.description")}
            detailText={
              customFieldCount === undefined
                ? undefined
                : t("applications.customField.configuredCount", {
                    count: customFieldCount,
                    defaultValue: "Configured fields: {{count}}",
                  })
            }
            learnMoreLink={t("applications.customField.learnMoreUrl")}
            learnMoreText={t("common.learnMore")}
            buttonText={customFieldButtonText}
            onButtonClick={onCustomFieldClick}
            icon={<TuneOutlined />}
            isConnected={hasCustomFields && customFieldLimit > 0}
            isLoading={isCustomFieldLoading}
            connectedLabel={t("common.configured", "Configured")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
      </ApplicationSection>
    );
  };

/** Stable fallback so the section does not re-render on every page render. */
const EMPTY_PLATFORMS: Array<string> = [];

/** MAAC offers only Shopify on the Japan server; mirror that here. */
const visibleEcommercePlatforms =
  SERVER_REGION === "jp"
    ? ECOMMERCE_PLATFORMS.filter((platform) => platform.type === "shopify")
    : ECOMMERCE_PLATFORMS;

const openMaacAppMarket = () => {
  window.open(`${MAAC_URL}/appmarketing`, "_blank", "noopener,noreferrer");
};

interface EcommerceSectionProps {
  connectedPlatforms: Array<string>;
  isLoading: boolean;
}

/**
 * E-commerce platforms are set up per LINE Official Account in MAAC's app
 * market, so every card sends the user there; Admin Center only reports which
 * platforms the organization has connected.
 */
export const EcommerceSection: React.FC<EcommerceSectionProps> =
  function EcommerceSection({ connectedPlatforms, isLoading }) {
    const { t } = useTranslation();

    if (!MAAC_URL) return null;

    return (
      <ApplicationSection
        title={t("applications.ecommerce.title", "E-commerce platforms")}
        description={t(
          "applications.ecommerce.description",
          "Sync members and orders from your online store to MAAC and CAAC. Set up each platform per LINE Official Account in MAAC.",
        )}
      >
        {visibleEcommercePlatforms.map((platform) => (
          <ApplicationCard
            key={platform.type}
            title={platform.name}
            description={t("applications.ecommerce.platformDescription", {
              platform: platform.name,
              defaultValue: "Sync members and orders from {{platform}}.",
            })}
            buttonText={t(
              "applications.ecommerce.setUpInMaac",
              "Set up in MAAC",
            )}
            onButtonClick={openMaacAppMarket}
            imageUrl={platform.imageUrl}
            isConnected={connectedPlatforms.includes(platform.type)}
            isLoading={isLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ))}
      </ApplicationSection>
    );
  };

interface AdvertisingSectionProps {
  enableGoogleAdsIntegration: boolean;
  enableLineAdsIntegration: boolean;
  enableMetaAdsIntegration: boolean;
  isGoogleAdsConnected: boolean;
  isGoogleAdsLoading: boolean;
  isLineAdsConnected: boolean;
  isLineAdsLoading: boolean;
  isMetaAdsConnected: boolean;
  isMetaAdsLoading: boolean;
  onGoogleAdsClick: () => void;
  onLineAdsClick: () => void;
  onMetaAdsClick: () => void;
  showGoogleAdsContactUs: boolean;
  showLineAdsContactUs: boolean;
  showMetaAdsContactUs: boolean;
}

const AdvertisingSection: React.FC<AdvertisingSectionProps> =
  function AdvertisingSection({
    enableGoogleAdsIntegration,
    enableLineAdsIntegration,
    enableMetaAdsIntegration,
    isGoogleAdsConnected,
    isGoogleAdsLoading,
    isLineAdsConnected,
    isLineAdsLoading,
    isMetaAdsConnected,
    isMetaAdsLoading,
    onGoogleAdsClick,
    onLineAdsClick,
    onMetaAdsClick,
    showGoogleAdsContactUs,
    showLineAdsContactUs,
    showMetaAdsContactUs,
  }) {
    const { t } = useTranslation();

    if (
      !enableGoogleAdsIntegration &&
      !enableMetaAdsIntegration &&
      !enableLineAdsIntegration
    )
      return null;

    return (
      <ApplicationSection
        title={t("applications.advertising.title", "Advertising")}
        description={t(
          "applications.advertising.description",
          "Connect advertising accounts to activate customer data and audiences.",
        )}
      >
        {enableGoogleAdsIntegration ? (
          <ApplicationCard
            title={t("applications.googleAds.title")}
            description={t("applications.googleAds.description")}
            learnMoreLink={t("applications.googleAds.learnMore.link")}
            learnMoreText={t("common.learnMore")}
            buttonText={
              showGoogleAdsContactUs
                ? t("common.contactUs")
                : t("common.connect")
            }
            onButtonClick={onGoogleAdsClick}
            imageUrl={GoogleAdsImage}
            isConnected={!showGoogleAdsContactUs && isGoogleAdsConnected}
            isLoading={isGoogleAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableMetaAdsIntegration ? (
          <ApplicationCard
            title={t("applications.metaAds.title")}
            description={t("applications.metaAds.description")}
            buttonText={
              showMetaAdsContactUs ? t("common.contactUs") : t("common.connect")
            }
            onButtonClick={onMetaAdsClick}
            imageUrl={MetaAdsImage}
            isConnected={!showMetaAdsContactUs && isMetaAdsConnected}
            isLoading={isMetaAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableLineAdsIntegration ? (
          <ApplicationCard
            title={t("applications.lineAds.title", "LINE Ads")}
            description={t(
              "applications.lineAds.description",
              "Connect your LINE Ads account to sync ad audiences.",
            )}
            buttonText={
              showLineAdsContactUs ? t("common.contactUs") : t("common.connect")
            }
            onButtonClick={onLineAdsClick}
            imageUrl={LineAdsBannerImage}
            isConnected={!showLineAdsContactUs && isLineAdsConnected}
            isLoading={isLineAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
      </ApplicationSection>
    );
  };

export const ApplicationsPage: FC = function ApplicationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const googleAdsManagerAccountQuery = interlude.googleAds.useGetManagerAccount(
    {
      queries: {
        checkAccess: false,
      },
    },
  );

  const isGoogleAdsConnected = googleAdsManagerAccountQuery.isSuccess;

  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();

  const productFeedListQuery = interlude.productFeed.useListFeeds();

  const enableGoogleAdsIntegration = useEnableGoogleAdsIntegrationMilestone1();

  const enableProductFeedCard = useEnableProductFeedCard();

  const enableMetaAdsIntegration = useEnableMetaAdsIntegration();

  const enableLineAdsIntegration = useEnableLineAdsIntegration();

  const enableCustomFieldCard = useEnableCustomFieldCard();

  const customFieldListQuery = interlude.customField.useListCustomFields({
    // Ask for single select fields and their options. Interlude leaves
    // them out unless a caller opts in.
    queries: { enable_single_select: true },
  });

  const metaAdsStatusQuery = interlude.metaAds.useGetStatus(undefined, {
    enabled: enableMetaAdsIntegration,
  });

  const isMetaAdsConnected =
    metaAdsStatusQuery.isSuccess &&
    metaAdsStatusQuery.data.tokenStatus === "active";

  const lineAdsGroupQuery = interlude.lineAds.useGetGroup(
    { queries: { checkAccess: false } },
    { enabled: enableLineAdsIntegration },
  );

  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const hasEcModule = hasEcModuleQuery.data?.hasEcModule ?? false;

  // Fail closed: absent level (legacy org) counts as not entitled.
  const isSegmentSyncAdPlatformOff =
    organizationSettingQuery.data?.maacFeatureSettings?.segmentSyncAdPlatform
      ?.level !== "on";

  const isAdIntegrationOff =
    organizationSettingQuery.data?.maacFeatureSettings?.adIntegration?.level !==
    "on";

  const showGoogleAdsContactUs = shouldShowGoogleAdsContactUs({
    segmentSyncOff: isSegmentSyncAdPlatformOff,
    adIntegrationOff: isAdIntegrationOff,
    hasEcModule,
  });

  const showMetaAdsContactUs = shouldShowMetaAdsContactUs({
    adIntegrationOff: isAdIntegrationOff,
  });

  const showLineAdsContactUs = shouldShowLineAdsContactUs({
    adIntegrationOff: isAdIntegrationOff,
  });

  const productFeedModuleEnabled =
    organizationSettingQuery.data?.productFeedModuleEnabled ?? false;

  const customFieldLimit = organizationSettingQuery.data?.customFieldLimit ?? 0;

  const hasProductFeeds = useMemo(() => {
    return (productFeedListQuery.data?.feeds.length ?? 0) > 0;
  }, [productFeedListQuery.data?.feeds.length]);

  const isProductFeedConnected = useMemo(() => {
    return productFeedModuleEnabled && hasProductFeeds;
  }, [productFeedModuleEnabled, hasProductFeeds]);

  const hasCustomFields = useMemo(() => {
    return (customFieldListQuery.data?.length ?? 0) > 0;
  }, [customFieldListQuery.data?.length]);

  const customFieldButtonText = useMemo(() => {
    if (customFieldLimit <= 0) return t("common.contactUs");
    if (hasCustomFields) return t("common.manage", "Manage");
    return t("applications.customField.setup");
  }, [hasCustomFields, customFieldLimit, t]);

  const handleGoogleAdsClick = () => {
    if (showGoogleAdsContactUs) {
      trackGaEvent("gadsContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/google-ads-integration" });
    }
  };

  const handleProductFeedClick = () => {
    if (productFeedModuleEnabled) {
      navigate({ to: "/applications/product-feeds" });
    } else {
      trackGaEvent("productFeedContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  const handleMetaAdsClick = () => {
    if (showMetaAdsContactUs) {
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/meta-ads-integration" });
    }
  };

  const handleLineAdsClick = () => {
    if (showLineAdsContactUs) {
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/line-ads-integration" });
    }
  };

  const handleCustomFieldClick = () => {
    if (customFieldLimit > 0) {
      navigate({ to: "/applications/custom-fields" });
    } else {
      trackGaEvent("customFieldContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <PageLayout>
      <PageTitle>
        {t("applications.dataIntegrations.title", "Data & integrations")}
      </PageTitle>
      <CustomerDataSection
        customFieldButtonText={customFieldButtonText}
        customFieldCount={
          customFieldLimit > 0 && customFieldListQuery.isSuccess
            ? customFieldListQuery.data.length
            : undefined
        }
        customFieldLimit={customFieldLimit}
        enableCustomFieldCard={enableCustomFieldCard}
        enableProductFeedCard={enableProductFeedCard}
        hasCustomFields={hasCustomFields}
        isCustomFieldLoading={
          customFieldListQuery.isPending || organizationSettingQuery.isPending
        }
        isProductFeedConnected={isProductFeedConnected}
        isProductFeedLoading={
          productFeedListQuery.isPending || organizationSettingQuery.isPending
        }
        onCustomFieldClick={handleCustomFieldClick}
        onProductFeedClick={handleProductFeedClick}
        productFeedCount={
          productFeedModuleEnabled && productFeedListQuery.isSuccess
            ? productFeedListQuery.data.feeds.length
            : undefined
        }
        productFeedModuleEnabled={productFeedModuleEnabled}
      />
      <EcommerceSection
        connectedPlatforms={
          hasEcModuleQuery.data?.ecPlatforms ?? EMPTY_PLATFORMS
        }
        isLoading={hasEcModuleQuery.isPending}
      />
      <AdvertisingSection
        enableGoogleAdsIntegration={enableGoogleAdsIntegration}
        enableLineAdsIntegration={enableLineAdsIntegration}
        enableMetaAdsIntegration={enableMetaAdsIntegration}
        isGoogleAdsConnected={isGoogleAdsConnected}
        isGoogleAdsLoading={
          googleAdsManagerAccountQuery.isPending ||
          hasEcModuleQuery.isPending ||
          organizationSettingQuery.isPending
        }
        isLineAdsConnected={lineAdsGroupQuery.isSuccess}
        isLineAdsLoading={
          lineAdsGroupQuery.isPending || organizationSettingQuery.isPending
        }
        isMetaAdsConnected={isMetaAdsConnected}
        isMetaAdsLoading={
          metaAdsStatusQuery.isPending || organizationSettingQuery.isPending
        }
        onGoogleAdsClick={handleGoogleAdsClick}
        onLineAdsClick={handleLineAdsClick}
        onMetaAdsClick={handleMetaAdsClick}
        showGoogleAdsContactUs={showGoogleAdsContactUs}
        showLineAdsContactUs={showLineAdsContactUs}
        showMetaAdsContactUs={showMetaAdsContactUs}
      />
    </PageLayout>
  );
};
